import {
  createCustomerSchema,
  updateCustomerSchema,
  type CreateCustomerInput,
  type Customer,
  type UpdateCustomerInput,
} from '@fdt/shared';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupsIcon from '@mui/icons-material/Groups';
import {
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { CrudPage } from '../components/CrudPage';
import type { Column } from '../components/DataTable';
import { ExportButton } from '../components/ExportButton';
import { MapPicker } from '../components/MapPicker';
import { StatusChip } from '../components/StatusChip';
import { customersApi } from '../resources';

interface FormState {
  code: string;
  businessName: string;
  vatNumber: string;
  fiscalCode: string;
  address: string;
  provincia: string;
  comune: string;
  cap: string;
  phone: string;
  email: string;
  contactPerson: string;
  notes: string;
  location: { lat: number; lng: number } | null;
  geofenceRadiusMeters: string;
  deliveryWindowStart: string;
  deliveryWindowEnd: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  code: '',
  businessName: '',
  vatNumber: '',
  fiscalCode: '',
  address: '',
  provincia: '',
  comune: '',
  cap: '',
  phone: '',
  email: '',
  contactPerson: '',
  notes: '',
  location: null,
  geofenceRadiusMeters: '',
  deliveryWindowStart: '',
  deliveryWindowEnd: '',
  isActive: true,
};

const FALLBACK_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

export function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [provinciaFilter, setProvinciaFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const filters = useMemo(
    () => ({
      provincia: provinciaFilter.trim().toUpperCase() || undefined,
      hasLocation: locationFilter || undefined,
    }),
    [provinciaFilter, locationFilter],
  );

  // The tile source is configurable in settings, so the map reads it from there.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: async () => (await api.get<{ osmTileUrl: string }>('/settings')).data,
    staleTime: 5 * 60 * 1000,
  });
  const tileUrl = settings.data?.osmTileUrl ?? FALLBACK_TILE_URL;

  const columns: Column<Customer>[] = [
    { id: 'code', label: t('fields.code'), render: (row) => row.code, sortable: true },
    {
      id: 'businessName',
      label: t('fields.businessName'),
      render: (row) => row.businessName,
      sortable: true,
    },
    { id: 'vatNumber', label: t('fields.vatNumber'), render: (row) => row.vatNumber ?? '—' },
    {
      id: 'comune',
      label: t('fields.comune'),
      render: (row) => [row.comune, row.provincia].filter(Boolean).join(' · ') || '—',
      sortable: true,
    },
    { id: 'phone', label: t('fields.phone'), render: (row) => row.phone ?? '—' },
    {
      id: 'location',
      label: t('fields.location'),
      render: (row) =>
        row.location ? (
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {row.location.lat.toFixed(4)}, {row.location.lng.toFixed(4)}
          </Typography>
        ) : (
          // A customer without a pin cannot be put on a route.
          <Chip size="small" variant="outlined" color="warning" label={t('common.missing')} />
        ),
    },
    {
      id: 'isActive',
      label: t('fields.status'),
      render: (row) => <StatusChip active={row.isActive} />,
    },
  ];

  return (
    <CrudPage<Customer, FormState, CreateCustomerInput, UpdateCustomerInput>
      title={t('nav.customers')}
      icon={GroupsIcon}
      tone="success"
      resourceKey="customers"
      api={customersApi}
      columns={columns}
      rowKey={(row) => row.id}
      emptyForm={EMPTY_FORM}
      defaultSortBy="businessName"
      filters={filters}
      schema={createCustomerSchema}
      updateSchema={updateCustomerSchema}
      formMaxWidth="lg"
      rowActions={(row) => (
        <Tooltip title={t('accounts.open')}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/customers/${row.id}/account`);
            }}
            aria-label={t('accounts.open')}
          >
            <AccountBalanceWalletIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      toForm={(row) => ({
        code: row.code,
        businessName: row.businessName,
        vatNumber: row.vatNumber ?? '',
        fiscalCode: row.fiscalCode ?? '',
        address: row.address ?? '',
        provincia: row.provincia ?? '',
        comune: row.comune ?? '',
        cap: row.cap ?? '',
        phone: row.phone ?? '',
        email: row.email ?? '',
        contactPerson: row.contactPerson ?? '',
        notes: row.notes ?? '',
        location: row.location,
        geofenceRadiusMeters: String(row.geofenceRadiusMeters),
        deliveryWindowStart: row.deliveryWindowStart?.slice(0, 5) ?? '',
        deliveryWindowEnd: row.deliveryWindowEnd?.slice(0, 5) ?? '',
        isActive: row.isActive,
      })}
      toPayload={(form) => ({
        code: form.code,
        businessName: form.businessName,
        // Empty optional text means "not set", which is null rather than ''.
        vatNumber: form.vatNumber || null,
        fiscalCode: form.fiscalCode || null,
        address: form.address || null,
        provincia: form.provincia || null,
        comune: form.comune || null,
        cap: form.cap || null,
        phone: form.phone || null,
        email: form.email || null,
        contactPerson: form.contactPerson || null,
        notes: form.notes || null,
        location: form.location,
        geofenceRadiusMeters: form.geofenceRadiusMeters || undefined,
        deliveryWindowStart: form.deliveryWindowStart || null,
        deliveryWindowEnd: form.deliveryWindowEnd || null,
        isActive: form.isActive,
      })}
      createTitle={t('customers.createTitle')}
      editTitle={t('customers.editTitle')}
      deleteTitle={t('customers.deleteTitle')}
      deleteMessage={(row) => t('customers.deleteMessage', { name: row.businessName })}
      toolbar={
        <>
          <TextField
            label={t('fields.provincia')}
            value={provinciaFilter}
            onChange={(event) => setProvinciaFilter(event.target.value.slice(0, 2))}
            sx={{ width: 110 }}
          />
          <TextField
            select
            label={t('fields.location')}
            value={locationFilter}
            onChange={(event) => setLocationFilter(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="true">{t('customers.pinned')}</MenuItem>
            <MenuItem value="false">{t('customers.unpinned')}</MenuItem>
          </TextField>
          <ExportButton
            file="customers.xlsx"
            params={{
              provincia: provinciaFilter.trim().toUpperCase(),
              hasLocation: locationFilter,
            }}
          />
        </>
      }
      renderForm={({ form, setForm, fieldErrors }) => (
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label={t('fields.code')}
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value })}
              error={Boolean(fieldErrors.code)}
              helperText={fieldErrors.code ? t(fieldErrors.code) : undefined}
              required
              sx={{ minWidth: 140 }}
            />
            <TextField
              label={t('fields.businessName')}
              value={form.businessName}
              onChange={(event) => setForm({ ...form, businessName: event.target.value })}
              error={Boolean(fieldErrors.businessName)}
              helperText={fieldErrors.businessName ? t(fieldErrors.businessName) : undefined}
              required
              fullWidth
            />
            <TextField
              select
              label={t('fields.status')}
              value={form.isActive ? 'true' : 'false'}
              onChange={(event) => setForm({ ...form, isActive: event.target.value === 'true' })}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="true">{t('common.active')}</MenuItem>
              <MenuItem value="false">{t('common.inactive')}</MenuItem>
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('fields.vatNumber')}
              value={form.vatNumber}
              onChange={(event) => setForm({ ...form, vatNumber: event.target.value })}
              error={Boolean(fieldErrors.vatNumber)}
              helperText={fieldErrors.vatNumber ? t(fieldErrors.vatNumber) : undefined}
              fullWidth
            />
            <TextField
              label={t('fields.fiscalCode')}
              value={form.fiscalCode}
              onChange={(event) => setForm({ ...form, fiscalCode: event.target.value })}
              error={Boolean(fieldErrors.fiscalCode)}
              helperText={fieldErrors.fiscalCode ? t(fieldErrors.fiscalCode) : undefined}
              fullWidth
            />
            <TextField
              label={t('fields.contactPerson')}
              value={form.contactPerson}
              onChange={(event) => setForm({ ...form, contactPerson: event.target.value })}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('fields.phone')}
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              error={Boolean(fieldErrors.phone)}
              helperText={fieldErrors.phone ? t(fieldErrors.phone) : undefined}
              fullWidth
            />
            <TextField
              label={t('fields.email')}
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email ? t(fieldErrors.email) : undefined}
              fullWidth
            />
          </Stack>

          <Divider />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('fields.address')}
              value={form.address}
              onChange={(event) => setForm({ ...form, address: event.target.value })}
              fullWidth
            />
            <TextField
              label={t('fields.comune')}
              value={form.comune}
              onChange={(event) => setForm({ ...form, comune: event.target.value })}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label={t('fields.provincia')}
              value={form.provincia}
              onChange={(event) => setForm({ ...form, provincia: event.target.value.slice(0, 2) })}
              error={Boolean(fieldErrors.provincia)}
              helperText={fieldErrors.provincia ? t(fieldErrors.provincia) : undefined}
              sx={{ width: 110 }}
            />
            <TextField
              label={t('fields.cap')}
              value={form.cap}
              onChange={(event) => setForm({ ...form, cap: event.target.value })}
              error={Boolean(fieldErrors.cap)}
              helperText={fieldErrors.cap ? t(fieldErrors.cap) : undefined}
              sx={{ width: 130 }}
            />
          </Stack>

          <MapPicker
            value={form.location}
            tileUrl={tileUrl}
            onChange={(location) => setForm({ ...form, location })}
            onAddressPicked={(address) =>
              setForm((current) => ({
                ...current,
                // Only fills blanks: a hand-typed address is not overwritten.
                address: current.address || address.street || '',
                comune: current.comune || address.comune || '',
                provincia: current.provincia || address.provincia || '',
                cap: current.cap || address.cap || '',
              }))
            }
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('fields.geofenceRadius')}
              value={form.geofenceRadiusMeters}
              onChange={(event) => setForm({ ...form, geofenceRadiusMeters: event.target.value })}
              error={Boolean(fieldErrors.geofenceRadiusMeters)}
              helperText={
                fieldErrors.geofenceRadiusMeters
                  ? t(fieldErrors.geofenceRadiusMeters)
                  : t('customers.geofenceHelp')
              }
              inputMode="numeric"
              fullWidth
            />
            <TextField
              label={`${t('fields.deliveryWindow')} ${t('common.from')}`}
              value={form.deliveryWindowStart}
              onChange={(event) => setForm({ ...form, deliveryWindowStart: event.target.value })}
              error={Boolean(fieldErrors.deliveryWindowStart)}
              helperText={fieldErrors.deliveryWindowStart ? t(fieldErrors.deliveryWindowStart) : 'hh:mm'}
              fullWidth
            />
            <TextField
              label={`${t('fields.deliveryWindow')} ${t('common.to')}`}
              value={form.deliveryWindowEnd}
              onChange={(event) => setForm({ ...form, deliveryWindowEnd: event.target.value })}
              error={Boolean(fieldErrors.deliveryWindowEnd)}
              helperText={fieldErrors.deliveryWindowEnd ? t(fieldErrors.deliveryWindowEnd) : 'hh:mm'}
              fullWidth
            />
          </Stack>

          <TextField
            label={t('fields.notes')}
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      )}
    />
  );
}
